import { zodResolver } from '@hookform/resolvers/zod';
import { Plus } from '@/core/ui/icons';
import { useState, type ReactNode } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { errorMessage } from '@/core/http';
import { Button, Field, Input, Select } from '@/core/ui';
import { useAddLesson, useAddModule } from '../application/queries';
import { LESSON_KIND_LABELS, LessonKind } from '../domain/course';
import {
  addLessonSchema,
  addModuleSchema,
  collapseSpaces,
  digitsOnly,
  emptyLesson,
  LIMITS,
  type AddLessonForm,
  type AddModuleForm,
} from '../domain/schemas';
import { formatted } from './formatted';

const tidy = { on: 'blur', apply: collapseSpaces } as const;

export function AddModuleForm({ courseId }: { courseId: string }) {
  const add = useAddModule(courseId);
  const form = useForm<AddModuleForm>({
    resolver: zodResolver(addModuleSchema),
    defaultValues: { title: '' },
  });
  const error = form.formState.errors.title?.message ?? (add.isError ? errorMessage(add.error) : null);

  const submit = form.handleSubmit(async ({ title }) => {
    await add.mutateAsync(title);
    form.reset();
  });

  return (
    <form onSubmit={submit} className="flex items-start gap-2" noValidate>
      <div className="flex-1">
        <Input
          placeholder="Título do novo módulo"
          aria-label="Título do novo módulo"
          aria-invalid={!!error}
          maxLength={LIMITS.title}
          {...formatted(form, 'title', tidy)}
        />
        {error && <p className="mt-1 text-xs text-fg">{error}</p>}
      </div>
      <Button type="submit" variant="secondary" loading={add.isPending}>
        <Plus aria-hidden className="size-4" />
        Módulo
      </Button>
    </form>
  );
}

/**
 * Envio do vídeo junto com a nova aula — injetado pelo backoffice (contexto
 * media). O catalog só sabe que existe um campo e quando chamar o envio.
 */
export interface NewLessonVideo {
  field: (state: {
    file: File | null;
    setFile: (f: File | null) => void;
    progress: number | null;
    error: string | null;
  }) => ReactNode;
  upload: (lessonId: string, file: File, onProgress: (ratio: number) => void) => Promise<unknown>;
  /** Duração em segundos lida do arquivo (metadados), para a aula já nascer com ela. */
  durationOf: (file: File) => Promise<number | null>;
  /** Por que o arquivo não pode ser enviado (formato, tamanho), ou `null`. */
  problemOf: (file: File) => string | null;
}

export function AddLessonForm({
  courseId,
  moduleId,
  video,
}: {
  courseId: string;
  moduleId: string;
  video?: NewLessonVideo;
}) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const add = useAddLesson(courseId);
  const form = useForm<AddLessonForm>({
    resolver: zodResolver(addLessonSchema),
    defaultValues: emptyLesson(),
    mode: 'onTouched',
  });
  const { errors } = form.formState;
  const kind = useWatch({ control: form.control, name: 'kind' });
  /** O campo de vídeo só existe para aula de vídeo, e só quando o backoffice o injeta. */
  const videoSlot = kind === LessonKind.VIDEO ? video : undefined;

  const close = () => {
    form.reset(emptyLesson());
    setFile(null);
    setProgress(null);
    setUploadError(null);
    add.reset();
    setOpen(false);
  };

  if (!open) {
    return (
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
        <Plus aria-hidden className="size-4" />
        Adicionar aula
      </Button>
    );
  }

  const submit = form.handleSubmit(async (values) => {
    setUploadError(null);
    // Arquivo recusado não cria aula pela metade: o problema aparece no campo.
    if (videoSlot && file && videoSlot.problemOf(file)) return;
    const before = new Set(add.data?.modules.find((m) => m.id === moduleId)?.lessons.map((l) => l.id));
    // Vídeo: a duração vem do arquivo, não de um campo digitado.
    const seconds = videoSlot && file ? await videoSlot.durationOf(file) : null;
    const course = await add.mutateAsync({ moduleId, form: values, seconds });
    // A aula nova é a que não existia antes neste módulo.
    const created = course.modules
      .find((m) => m.id === moduleId)
      ?.lessons.filter((l) => !before.has(l.id))
      .at(-1);
    if (videoSlot && file && created) {
      try {
        setProgress(0);
        await videoSlot.upload(created.id, file, setProgress);
      } catch (e) {
        // A aula já existe: o vídeo pode ser reenviado pela linha dela.
        setProgress(null);
        // O media explica em português o que houve (formato, rede, storage).
        setUploadError(
          `Aula criada, mas o vídeo não foi enviado: ${e instanceof Error ? e.message : errorMessage(e)}`,
        );
        return;
      }
    }
    close();
  });

  const busy = add.isPending || progress !== null;

  return (
    <form
      onSubmit={submit}
      noValidate
      aria-label="Nova aula"
      className="w-full space-y-5 rounded-xl border border-line bg-surface-2/40 p-5"
    >
      <p className="text-sm font-semibold text-fg">Nova aula</p>
      <div className="grid gap-4 sm:grid-cols-[1fr_11rem]">
        <Field label="Título da aula" error={errors.title?.message}>
          {(p) => <Input {...p} autoFocus maxLength={LIMITS.title} {...formatted(form, 'title', tidy)} />}
        </Field>
        <Field label="Tipo">
          {(p) => (
            <Select {...p} {...form.register('kind')}>
              {Object.values(LessonKind).map((k) => (
                <option key={k} value={k}>
                  {LESSON_KIND_LABELS[k]}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>

      {videoSlot ? (
        videoSlot.field({ file, setFile, progress, error: uploadError })
      ) : (
        <Field label="Duração (min)" error={errors.duration?.message} hint="Opcional. Até 1.440 min.">
          {(p) => (
            <Input
              {...p}
              inputMode="numeric"
              placeholder="0"
              maxLength={4}
              className="sm:w-40"
              {...formatted(form, 'duration', { on: 'change', apply: digitsOnly })}
            />
          )}
        </Field>
      )}

      <Field label="Descrição (opcional)" error={errors.description?.message}>
        {(p) => <Input {...p} maxLength={LIMITS.description} {...form.register('description')} />}
      </Field>
      <label className="flex items-center gap-2 text-sm text-fg-muted">
        <input type="checkbox" className="size-4 accent-teal-500" {...form.register('required')} />
        Aula obrigatória para conclusão
      </label>
      {add.isError && (
        <p role="alert" className="text-sm text-fg">
          {errorMessage(add.error)}
        </p>
      )}
      <div className="flex gap-2">
        <Button type="submit" loading={busy}>
          {videoSlot && file ? 'Salvar e enviar vídeo' : 'Salvar aula'}
        </Button>
        <Button variant="ghost" onClick={close} disabled={progress !== null}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}
